import axios from 'axios'
import { EzClass } from '/ezlibrary/EzClass.js';
import {
    EzObject,
    EzNumber,
    EzBoolean,
    EzString,
    EzArray,
    EzFunction,
    EzUrl,
    EzJson,
    EzPromise
} from '/ezlibrary/helpers/EzHelpers.js';
import {
    EzRegistrationState
} from '/ezlibrary/enums/EzEnumerations.js';
import { EzHttpHelper } from '/public/javascript/common/ezclocker-http-helper.js';
import { EzVersionChecker } from '/ezlibrary/EzVersionChecker.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides website navigation utility methods
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for ready event:
 *     globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for ready event:
 *     EzClass.ezWaitReady(
 *         EzNavigation.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Prefered access via EzNavigation static function, fields and properties.
 * Deprecated access via singleton instance:
 *      fromn within EzNavigation class: EzNavigation.ezInstance
 *      from outside of EzNavigation class: globalThis.ezApi.ezclocker.ezNavigation
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzNavigation extends EzClass {
    /**
     * @static
     * @public @method
     * Instructs the browser to reload the current page.
     */
    static reloadPage() {
        window.location.reload();
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Path Variable Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_employerId]'
     * @returns {string}
     */
    static get EZPathVar_employerId() {
        return '[EZPV_employerId]';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_employeeId]'
     * @returns {string}
     */
    static get EZPathVar_employeeId() {
        return '[EZPV_employeeId]';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_userId]'
     * @returns {string}
     */
    static get EZPathVar_userId() {
        return '[EZPV_userId]';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_id]'
     * @returns {string}
     */
    static get EZPathVar_id() {
        return '[EZPV_id]';
    }


    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Mobile API Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @public @method @static @readonly @property
     * Gets string const '/mobile/public'
     * @returns {string}
     */
    static get MOBILE_PUBLIC_PATH() {
        return '/mobile/public';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/mobile/secure'
     * @returns {string}
     */
    static get MOBILE_SECURE_PATH() {
        return '/mobile/secure';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/m'
     * @returns {string}
     */
    static get NEW_MOBILE_PATH() {
        return '/m';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const `${EzNavigation.NEW_MOBILE_PATH}/p`
     * @returns {string}
     */
    static get NEW_MOBILE_PUBLIC_PATH() {
        return `${EzNavigation.NEW_MOBILE_PATH}/p`;
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const `${EzNavigation.NEW_MOBILE_PATH}/s`
     * @returns {string}
     */
    static get NEW_MOBILE_SECURE_PATH() {
        return `${EzNavigation.NEW_MOBILE_PATH}/s`;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website's mobile path (aka /m)
     * @returns {string}
     */
    static get EZCLOCKER_WEBSITE_MOBILE_PATH() {
        return '/m';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website's mobile public path (aka /m/p)
     * @returns {string}
     */
    static get EZCLOCKER_WEBSITE_MOBILE_PUBLIC_PATH() {
        return `${EzNavigation.EZCLOCKER_WEBSITE_MOBILE_PATH}/p`;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the EzClocker website's mobile secure path (aka /m/s)
     * @returns {string}
     */
    static get EZCLOCKER_WEBSITE_MOBILE_SECURE_PATH() {
        return `${EzNavigation.EZCLOCKER_WEBSITE_MOBILE_PATH}/s`;
    }


    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Start of Google Play and Andriod Related Constants
     */

    /**
     * @static
     * @public @readonly @property
     * Gets the default Google Play Store URL for EzClocker Business android Mobile App
     * Value: 'https://play.google.com/store/apps/details?id=com.ezclocker'
     * @returns {string}
     */
    static get DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_BUSINESS_URL() {
        return 'https://play.google.com/store/apps/details?id=com.ezclocker';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Google Play Store URL for EzClocker Business android Mobile App
     * @returns {string}
     */
    static get googlePlayAppStoreEzClockerBusinessUrl() {
        return EzNavigation.DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_BUSINESS_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default Google Play Store URL for EzClocker Employee (aka EzClocker Business) android Mobile App
     * Value: 'https://play.google.com/store/apps/details?id=com.ezclocker'
     * @returns {string}
     */
    static get DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_EMPLOYEE_URL() {
        return 'https://play.google.com/store/apps/details?id=com.ezclocker';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Google Play Store URL for EzClocker Employee (aka EzClocker Business) android Mobile App
     * @returns {string}
     */
    static get googlePlayAppStoreEzClockerEmployeeUrl() {
        return EzNavigation.DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_EMPLOYEE_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default Google Play Store URL for EzClocker Personal android Mobile App
     * Value: EzString.EMPTY
     * @returns {string}
     */
    static get DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_PERSONAL_URL() {
        // No personal app for android
        return EzString.EMPTY;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Google Play Store URL for EzClocker Personal android Mobile App
     * @returns {string}
     */
    static get googlePlayAppStoreUrlEzClockerPersonal() {
        return EzNavigation.DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_PERSONAL_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets default the Google Play Store URL for EzClocker Kiosk android Mobile App
     * Value: 'https://play.google.com/store/apps/details?id=com.ezclockerkiosk'
     * @returns {string}
     */
    static get DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_KIOSK_URL() {
        return 'https://play.google.com/store/apps/details?id=com.ezclockerkiosk';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Google Play Store URL for EzClocker Kiosk android Mobile App
     * @returns {string}
     */
    static get googlePlayAppStoreEzClockerKioskMobileUrl() {
        return EzNavigation.DEFAULT_GOOGLE_PLAY_APPSTORE_EZCLOCKER_KIOSK_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default demo url for EzClocker Buisness iOS app
     * @returns {string}
     */
    static get DEFAULT_ANDROID_EZCLOCKER_BUSINESS_DEMO_URL() {
        return 'https://vimeo.com/1057261745/84d8dfb9a7';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured demo url for EzClocker Buisness iOS app
     * @returns {string}
     */
    static get androidEzClockerBusinessDemoUrl() {
        return EzNavigation.DEFAULT_ANDROID_EZCLOCKER_BUSINESS_DEMO_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default Google Play Store badge image url
     * @returns {string}
     */
    static get DEFAULT_GOOGLE_PLAY_STORE_BADGE_IMG_URL() {
        return 'https://play.google.com/intl/en_us/badges/images/badge_new.png';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Google Play Store badge image url
     * @returns {string}
     */
    static get googlePlayStoreBadgeImgUrl() {
        return EzNavigation.DEFAULT_GOOGLE_PLAY_STORE_BADGE_IMG_URL;
    }

    /*
     * End of Google Play and Andriod Related Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Start of Apple App Store, iOS, and iPadOS Related Constants
     */

    /**
     * @static
     * @public @readonly @property
     * Gets the default Apple App Store URL for EzClocker Business
     * Value: 'https://apps.apple.com/us/app/ezclocker-employee-time-track/id800807197'
     * @returns {string}
     */
    static get DEFAULT_APPLE_APPSTORE_EZCLOCKER_BUSINESS_URL() {
        return 'https://apps.apple.com/us/app/ezclocker-employee-time-track/id800807197';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Apple App Store URL for EzClocker Business
     * @returns {string}
     */
    static get appleAppStoreEzClockerBusinessUrl() {
        return EzNavigation.DEFAULT_APPLE_APPSTORE_EZCLOCKER_BUSINESS_URL;
    }

    /**
    * @static
    * @public @readonly @property
    * Gets the default Apple App Store URL for EzClocker Kiosk iPad OS App
    * Value: 'https://apps.apple.com/us/app/ezclocker-kiosk-time-tracking/id1339692641'
    * @returns {string}
    */
    static get DEFAULT_APPLE_APPSTORE_EZCLOCKER_KIOSK_URL() {
        return 'https://apps.apple.com/us/app/ezclocker-kiosk-time-tracking/id1339692641';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Apple App Store URL for EzClocker Kiosk iPad OS App
     * @returns {string}
     */
    static get appleAppStoreEzClockerKioskUrl() {
        return EzNavigation.DEFAULT_APPLE_APPSTORE_EZCLOCKER_KIOSK_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default Apple App Store URL for EzClocker Personal iOS App
     * Value: 'https://apps.apple.com/us/app/ezclocker-personal-timecard/id833047956'
     * @returns {string}
     */
    static get DEFAULT_APPLE_APPSTORE_EZCLOCKER_PERSONAL_URL() {
        return 'https://apps.apple.com/us/app/ezclocker-personal-timecard/id833047956';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Apple App Store URL for EzClocker Personal iOS App
     * @returns {string}
     */
    static get appleAppStoreEzClockerPersonalUrl() {
        return EzNavigation.DEFAULT_APPLE_APPSTORE_EZCLOCKER_PERSONAL_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default Apple App Store URL for the ezClocker Personal iOS app.
     * Value: 'ezlogin://signin'
     * @returns {string}
     */
    static get DEFAULT_IOS_EZCLOCKER_SIGNIN_URL() {
        return 'ezlogin://signin';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Apple App Store URL for the ezClocker Personal iOS app.
     * @returns {string}
     */
    static get iOSEzClockerSignInUrl() {
        return EzNavigation.DEFAULT_IOS_EZCLOCKER_SIGNIN_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the Default Apple app atore URL for the ezClocker Personal iOS app.
     * Value: 'ezlogin://signin'
     * @returns {string}
     */
    static get DEFAULT_IPADOS_EZCLOCKER_SIGNIN_URL() {
        return 'ezlogin://signin';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the Default Apple app atore URL for the ezClocker Personal iOS app.
     * @returns {string}
     */
    static get iPadOsEzClockerSignInUrl() {
        return EzNavigation.DEFAULT_IPADOS_EZCLOCKER_SIGNIN_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default demo url for EzClocker Buisness iOS app
     * Value: 'https://vimeo.com/1066341714/e454411e74'
     * @returns {string}
     */
    static get DEFAULT_APPLE_EZCLOCKER_BUSINESS_DEMO_URL() {
        return 'https://vimeo.com/1066341714/e454411e74';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured demo url for EzClocker Buisness iOS app
     * @returns {string}
     */
    static get appleEzClockerBusinessDemoUrl() {
        return EzNavigation.DEFAULT_APPLE_EZCLOCKER_BUSINESS_DEMO_URL;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default Apple App Stopre badge image url
     * Value: EzNavigation.buildPublicImageUrl('marketing/apple-appstore.svg'
     * @returns {string}
     */
    static get DEFAULT_APPLE_APP_STORE_BADGE_IMG_URL() {
        return EzNavigation.buildPublicImageUrl('marketing/apple-appstore.svg');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the configured Apple App Stopre badge image url
     * @returns {string}
     */
    static get appleAppStoreBadgeImgUrl() {
        return EzNavigation.DEFAULT_APPLE_APP_STORE_BADGE_IMG_URL;
    }

    /*
     * End of Apple App Store, iOS, and iPadOS Related Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Start of ezClocker Website Releated Constants
     */

    /**
    * @static
    * @public @readonly @property
    * Gets the ezClocker website and services default domain (for production)
    * Value: 'localhost'
    * @returns {string}
    */
    static get DEFAULT_LOCALHOST_EZCLOCKER_WEBSITE_DMAIN() {
        return 'localhost';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website and services default domain (for production)
     * Value: 'loc.ezclocker.com'
     * @returns {string}
     */
    static get DEFAULT_LOC_EZCLOCKER_WEBSITE_DMAIN() {
        return 'loc.ezclocker.com';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website and services default domain (for production)
     * Value: 'dev.ezclocker.com'
     * @returns {string}
     */
    static get DEFAULT_DEV_EZCLOCKER_WEBSITE_DOMAIN() {
        return 'dev.ezclocker.com';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website and services default domain (for production)
     * Value: 'qal.ezclocker.com'
     * @returns {string}
     */
    static get DEFAULT_QAL_EZCLOCKER_WEBSITE_DMAIN() {
        return 'qal.ezclocker.com';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website and services default domain (for production)
     * Value: 'e2e.ezclocker.com'
     * @returns {string}
     */
    static get DEFAULT_E2E_EZCLOCKER_WEBSITE_DMAIN() {
        return 'e2e.ezclocker.com';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website and services default domain (for production)
     * Value: 'prf.ezclocker.com'
     * @returns {string}
     */
    static get DEFAULT_PRF_EZCLOCKER_WEBSITE_DMAIN() {
        return 'prf.ezclocker.com';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezClocker website and services default domain (for production)
     * Value: 'ezclocker.com'
     * @returns {string}
     */
    static get DEFAULT_PRD_EZCLOCKER_WEBSITE_DMAIN() {
        return 'ezclocker.com';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/resources/j_spring_security_check'
     * @returns {string}
     */
    static get SIGN_IN_PATH() {
        return '/resources/j_spring_security_check';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/resources/j_spring_security_logout'
     * @returns {string}
     */
    static get SIGN_OUT_PATH() {
        return '/resources/j_spring_security_logout';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/secure'
     * @returns {string}
     */
    static get WEBSITE_SECURE_PATH() {
        return '/secure';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/public'
     * @returns {string}
     */
    static get WEBSITE_PUBLIC_PATH() {
        return '/public';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '_api'
     * @returns {string}
     */
    static get INTERNAL_API_PATH_PREFIX() {
        return '_api';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '_'
     * @returns {string}
     */
    static get INTERNAL_CLASSIC_API_PATH_PREFIX() {
        return '_';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const 'api'
     * @returns {string}
     */
    static get PUBLIC_API_PATH_PREFIX() {
        return 'api';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const 'v1'
     * @returns {string}
     */
    static get DEFAULT_INTERNAL_API_VERSION() {
        return 'v1';
    }

    /*
     * End of ezClocker Website Releated Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @public @readonly @property
     * Gets string const revision value
     * @returns {string}
     * @deprecated
     * Migrate to: EzVersionChecker.ezWebsiteVersion
     */
    get revisionVersion() {
        return EzVersionChecker.ezWebsiteVersion;
    }

    /**
     * @private @field
     * Stores the value of isDeveloperDocs boolean
     * Default: false
     * @type {boolean}
     */
    static #ezIsDeveloperDocs = false;
    /**
     * @public @property @getter
     * Gets the value of isDeveloperDocs boolean
     * @returns {boolean}
     */
    static get ezIsDeveloperDocs() {
        return EzBoolean.booleanOrFalse(this.#ezIsDeveloperDocs);
    }
    /**
     * @public @property @getter
     * Sets the value of isDeveloperDocs boolean
     * @param {boolean} isDeveloperDocs
     * Default: false
     */
    static set ezIsDeveloperDocs(isDeveloperDocs = false) {
        this.#ezIsDeveloperDocs = EzBoolean.booleanOrFalse(isDeveloperDocs);
    }
    /**
     * @public @property @getter
     * Gets the value of isDeveloperDocs boolean
     * @returns {boolean}
     */
    get isDeveloperDocs() {
        return EzBoolean.booleanOrFalse(EzNavigation.ezIsDeveloperDocs);
    }
    /**
     * @public @property @getter
     * Sets the value of isDeveloperDocs boolean
     * @param {boolean} isDeveloperDocs
     * Default: false
     */
    set isDeveloperDocs(isDeveloperDocs = false) {
        EzNavigation.ezIsDeveloperDocs = EzBoolean.booleanOrFalse(isDeveloperDocs);
    }

    /**
     * @private @field
     * Stores if the file version is enabled or not
     * Default: true
     * @type {boolean}
     */
    static #revisionVersionEnabled = true;
    /**
     * @public @property @getter
     * Gets if the file version is enabled or not
     * @returns {boolean}
     */
    static get revisionVersionEnabled() {
        return EzBoolean.booleanOrTrue(this.#revisionVersionEnabled);
    }
    /**
     * @public @property @getter
     * Sets if the file version is enabled or not
     * @param {boolean} revisionVersionEnabled
     * Default: true
     */
    static set revisionVersionEnabled(revisionVersionEnabled = true) {
        this.#revisionVersionEnabled = EzBoolean.booleanOrTrue(revisionVersionEnabled);
    }

    /**
     * @public @property @getter
     * Gets if the file version is enabled or not
     * @returns {boolean}
     * @deprecated
     * Migrate to static property: EzNavigation.revisionVersionEnabled
     */
    get enableFileRevision() {
        return EzNavigation.revisionVersionEnabled;
    }
    /**
     * @public @property @getter
     * Sets if the file version is enabled or not
     * @param {boolean} enableFileRevision
     * Default: true
     * @deprecated
     * Migrate to static property: EzNavigation.revisionVersionEnabled
     */
    set enableFileRevision(enableFileRevision = true) {
        EzNavigation.revisionVersionEnabled = enableFileRevision;
    }

    /**
     * @private @field
     * Stores the currently configured environment id (lowercase)
     * Default: 'prd'
     * @type {string}
     */
    static #environment = 'prd';
    /**
     * @public @method @getter @property
     * Gets the currently configured environment id (lowercase)
     * @returns {string}
     */
    static get environment() {
        return EzString.stringWithTextOrDefault(
            this.#environment,
            'prd');
    }
    /**
     * @public @method @setter @property
     * Sets the currently configured environment id (lowercase)
     * @param {string} environment
     * Default: 'prd'
     */
    static set environment(environment = 'prd') {
        this.#environment = EzString.stringWithTextOrDefault(
            environment,
            'prd');
    }
    /**
     * @public @method @getter @property
     * Gets the currently configured environment id (lowercase)
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzNavigation.environment
     */
    get ezEnvironment() {
        return EzString.stringWithLengthOrDefault(
            EzNavigation.environment,
            'prd');
    }
    /**
     * @public @method @setter @property
     * Sets the currently configured environment id (lowercase)
     * @param {string} environment
     * Default: 'prd'
     * @deprecated
     * Migrate to static property: EzNavigation.environment
     */
    set ezEnvironment(environment = 'prd') {
        EzNavigation.environment = environment;
    }

    /**
     * @static
     * @private @field
     * Stores if EzNavigation is initialized yet or not
     * @type {boolean}
     */
    static #initialized = false;
    /**
     * @static
     * @public @property @getter
     * Gets if EzNavigation is initialized yet or not
     * @return {boolean}
     */
    static get initialized() {
        return this.#initialized;
    }
    /**
     * @static
     * @public @property @setter
     * Sets if EzNavigation is initialized yet or not
     * @param {boolean} initialized
     * Default: false
     */
    static set initialized(initialized = false) {
        this.#initialized = EzBoolean.booleanOrFalse(initialized);
    }

    /**
     * @public @method
     * Initializes EzNavigation
     * @returns {EzNavigation}
     */
    ezInit() {
        if (!EzNavigation.initialized) {
            EzNavigation.ezInstance.ezLoadEnvironmentId();
        }

        return EzNavigation.ezInstance;
    }

    /**
     * @static
     * @public @method
     * Obtains the current environment
     * @returns {Promise.resolve}
     * Resolve contains the lower case environment id
     */
    static loadEnvironmentId() {
        return EzPromise.asyncAction(
            (finished) => axios.get(EzNavigation.buildPublicApiUrl('version', 'v1'))
                .then(
                    (response) => {
                        if (EzString.hasText(response.data.environment)) {
                            EzNavigation.environment = response.data.environment.toLowerCase();
                        }

                        if (!EzString.hasText(EzNavigation.environment) && EzString.hasText(response.data.targetEnvironment)) {
                            EzNavigation.environment = response.data.targetEnvironment.toLowerCase();
                        }

                        if (!EzString.hasText(EzNavigation.environment)) {
                            EzNavigation.environment = globalThis.ezApi.ezclocker.ezUrlHelper.ezGetEnvironmentFromUrl().toLowerCase();
                        }

                        if (!EzString.hasText(EzNavigation.environment)) {
                            EzNavigation.environment = 'prd';
                        }

                        return finished(EzNavigation.environment);
                    })
                .catch(
                    (err) => {
                        globalThis.ezApi.ezclocker.ezLogger.warn(
                            EzString.em`
                                EzNavigation: Environment information was not available. Defaulting to enviornment PRD
                                Error response: ${globalThis.ezApi.ezToJson(err)}`);

                        EzNavigation.environment = 'prd';

                        return finished(EzNavigation.environment);
                    }));
    }

    /**
     * @public @method
     * Obtains the current environment
     * @returns {Promise.resolve}
     * Resolve contains the lower case environment id
     * @deprecated
     * Migrate to: EzNavigation.loadEnvironmentId()
     */
    ezLoadEnvironmentId = EzNavigation.loadEnvironmentId;

    /**
     * @public @method
     * Gets the ezClocker website's landing page url
     * @returns {string}
     */
    static get ezClockerLandingPageUrl() {
        return EzNavigation.buildBaseUrl();
    }


    /**
     * @public @method
     * Returns the ezClocker base url
     * @returns {string}
     */
    static buildBaseUrl() {
        const baseUrl = window.location.origin;

        const pathItemsArray = EzString.stringWithTextOrEmpty(window.location.pathname).split('/');

        return EzNumber.TWO <= pathItemsArray.length && 'ezclocker' === pathItemsArray[EzNumber.ONE].toLowerCase()
            ? `${baseUrl}/${pathItemsArray[EzNumber.ONE]}`
            : baseUrl;
    }

    /**
     * @public @method
     * Returns the ezClocker base url
     * @returns {string}
     * @deprecated
     * Migrate to static function: zNavigation.buildBaseUrl()
     */
    ezGetBaseUrl = EzNavigation.buildBaseUrl;

    /**
     * @static
     * @public @function
     * Updates the search value in the window.navigation.search property
     * @param {undefined|null|string} newSearchValue
     * Do not include the leading '?'
     * @returns {string}
     * Updated windows.location.href value
     */
    static setWindowNavigatioNSearchValue(newSearchValue = EzString.EMPTY) {
        if (EzString.hasText(newSearchValue)) {
            window.history.pushState(
                {},
                window.document.title,
                newSearchValue.startsWith('?')
                    ? `${window.location.origin}${window.location.pathname}${newSearchValue}`
                    : `${window.location.origin}${window.location.pathname}?${newSearchValue}`);
        } else {
            window.history.pushState(
                EzObject.EMPTY,
                window.document.title,
                `${window.location.origin}${window.location.pathname}`);
        }

        return window.location.href;
    }

    /**
     * @public @function
     * Updates the search value in the window.navigation.search property
     * @param {undefined|null|string} newSearchValue
     * Do not include the leading '?'
     * @returns {string}
     * Updated windows.location.href value
     * @deprecated
     * Migrate to static function: EzNavigation.setWindowNavigatioNSearchValue(newSearchValue = EzString.EMPTY)
     */
    ezSetWindowNavigatioNSearchValue = EzNavigation.setWindowNavigatioNSearchValue;

    /**
     * @static
     * @public @method
     * Returns the URL in the current window.location.origin
     * @returns {string}
     */
    static getLocationUrl() {
        return window.location.href.toString();
    }

    /**
     * @public @method
     * Returns the URL in the current window.location.origin
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.getLocationUrl()
     */
    ezGetLocationUrl = EzNavigation.getLocationUrl;

    /**
     * @static
     * @public @method
     * Builds a url and then applies the file revision param.
     * @param {string} baseUrl
     * @param {array} pathParts
     * Default: empty array
     * @param {undefined|null|boolean} noFileRevision
     * Default: false
     */
    static buildUrl(baseUrl = '/', pathParts = [], noFileRevision = true) {
        let path = EzUrl.postSlash(EzString.stringWithTextOrEmpty(baseUrl));

        if (EzArray.hasLength(pathParts)) {
            for (let pathPart of pathParts) {
                if (EzString.hasText(pathPart)) {
                    if (path.endsWith('/') && pathPart.startsWith('/')) {
                        // Remove / from pathPart as it already exists in path ending
                        path = `${path}${pathPart.substr(EzNumber.ONE)}`;
                    } else if (!path.endsWith('/') && !pathPart.startsWith('/')) {
                        // Add the missing path separator between path and pathPart
                        path = `${path}/${pathPart}`;
                    } else {
                        // Path separator should be good to go
                        path = `${path}${pathPart}`;
                    }
                }
            }
        }

        return EzBoolean.booleanOrFalse(noFileRevision)
            ? path
            : EzNavigation.applyRevisionVersionToUrl(path);
    }

    /**
     * @public @method
     * Builds a url and then applies the file revision param.
     * @param {string} baseUrl
     * @param {array} pathParts
     * Default: empty array
     * @param {undefined|null|boolean} noFileRevision
     * Default: false
     * @deprecated
     * Migrate to static function: EzNavigation.buildUrl(baseUrl = '/', pathParts = [], noFileRevision = false)
     */
    ezBuildUrl = EzNavigation.buildUrl;

    /**
     * @static
     * @public @method
     * Returns the url for a dynamic page. Example: https://ezclocker.com/dynamic/{pagename}
     * @param {string} pageName
     * @returns {string}
     */
    static ezGetDynamicPageUrl(pageName) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                'dynamic',
                EzString.stringWithTextOrEmpty(pageName)
            ]);
    }

    /**
     * @public @method
     * Returns the url for a dynamic page. Example: https://ezclocker.com/dynamic/{pagename}
     * @param {string} pageName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.ezGetDynamicPageUrl(pageName)
     */
    ezGetDynamicPageUrl = EzNavigation.ezGetDynamicPageUrl;

    /**
     * @static
     * @public @function
     * Gets a url from the base url and the provided url snip.
     * Example: EzNavigation.buidlUrlFromBase('welcome') -> https://ezclocker.com/welcome
     * @param {string} urlSnip
     * @returns {string}
     */
    static buidlUrlFromBase(urlSnip) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzString.stringWithTextOrEmpty(urlSnip)
            ]);
    }

    /**
     * @public @function
     * Gets a url from the base url and the provided url snip.
     * Example: EzNavigation.ezGetUrlFromBase('welcome') -> https://ezclocker.com/welcome
     * @param {string} urlSnip
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buidlUrlFromBase(urlSnip)
     */
    ezGetUrlFromBase = EzNavigation.buidlUrlFromBase;

    /**
     * @public @method
     * Returns the environment the website is running within
     * @returns {Promise.resolve}
     */
    static get websiteEnvironment() {
        if (EzString.hasText(EzNavigation.environment)) {
            return EzPromise.resolve(EzNavigation.ezInstance.environment);
        }

        return EzPromise.asyncAction(
            (finished) => {
                globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                    EzNavigation.buildUrl(
                        EzNavigation.buildBaseUrl(),
                        [
                            'environment'
                        ]))
                    .then(
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                    .then(
                        (response) => {
                            EzNavigation.ezInstance.environment = response.message;

                            return finished(EzNavigation.ezInstance.environment);
                        },
                        (eResponse) => {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                EzString.em`
                                    Failed to determine the current environment.
                                    [Error response: ${globalThis.ezApi.ezclocker.ezHttpHelper.extractErrorResponseMessage(eResponse)}]`);

                            return finished('PRD');
                        });
            });
    }

    /**
     * @public @method
     * Returns the environment the website is running within
     * @returns {Promise.resolve}
     * @deprecated
     * Migrate to static property: EzNavigation.websiteEnvironment
     */
    ezGetEnvironment = () => EzNavigation.websiteEnvironment;

    /**
     * @static
     * @public @method
     * Navigates to the provided URL
     * @param {string} url
     * @param {undefined|null|boolean} skipRevision
     * Default: false
     * @returns {string}
     */
    static navTo(url, skipRevision = false) {
        const urlToUse = EzBoolean.booleanOrFalse(skipRevision)
            ? url
            : EzNavigation.applyRevisionVersionToUrl(url);

        if (EzFunction.isFunction(window.location?.assign)) {
            return window.location.assign(urlToUse);
        }

        window.location = urlToUse;

        return window.location;
    }

    /**
     * @public @method
     * Navigates to the provided URL
     * @param {string} url
     * @param {undefined|null|boolean} skipRevision
     * Default: false
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.navTo(url, skipRevision = false)
     */
    ezNavigateTo = EzNavigation.navTo;

    /**
     * @static
     * @public @method
     * If website revsion version is enabled, the url is returned with the file revision param appended:
     * Template: `${url}${url.includes('?') ? '&' : '?'}${EzVersionChecker.ezWebsiteRevisionVersion}`
     * Otherwise, the url is returned as provided.
     * @param {undefined|null|string} url
     * @returns {string}
     */
    static applyRevisionVersionToUrl(url) {
        return EzBoolean.isTrue(EzNavigation.revisionVersionEnabled) &&
            EzString.hasText(EzVersionChecker.ezWebsiteRevisionVersion) &&
            EzString.hasText(url) &&
            !url.includes(EzVersionChecker.ezWebsiteRevisionVersion)
            ? `${url}${url.includes('?') ? '&' : '?'}${EzVersionChecker.ezWebsiteRevisionVersion}`
            : EzString.stringOrEmpty(url);
    }

    /**
     * @public @method
     * If website revsion version is enabled, the url is returned with the file revision param appended:
     *      Template for URL without params........: `${url}?${EzVersionChecker.ezWebsiteRevisionVersion}`
     *      Template for URL with existing params..: `${url}&${EzVersionChecker.ezWebsiteRevisionVersion}`
     * Otherwise, the url is returned as provided.
     * @param {undefined|null|string} url
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzNavigation.applyRevisionVersionToUrl(url)
     */
    ezApplyFileRevision = EzNavigation.applyRevisionVersionToUrl;

    /**
     * @static
     * @public @method
     * @param {string} pageName
     * @returns {string}
     */
    static buildSecurePageUrl(pageName) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.WEBSITE_SECURE_PATH,
                EzString.stringWithTextOrEmpty(pageName)
            ]);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildSecurePageUrl(pageName)
     */
    ezGetSecurePageUrl = EzNavigation.buildSecurePageUrl;

    /**
     * @static
     * @public @method
     * Builds a url for the websites public folder.
     * Result Template: {EzNavigation.buildBaseUrl()}/EzNavigation.ezInstance.WEBSITE_PUBLIC_PATH/{urlSuffix}
     * Example: https://ezclocker.com/public/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {string}
     */
    static buildPublicUrl(urlSuffix) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.WEBSITE_PUBLIC_PATH,
                EzString.stringWithTextOrEmpty(urlSuffix)
            ]);
    }

    /**
     * @public @method
     * Builds a url for the websites public folder.
     * Result Template: {EzNavigation.buildBaseUrl()}/EzNavigation.ezInstance.WEBSITE_PUBLIC_PATH/{urlSuffix}
     * Example: https://ezclocker.com/public/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzNavigation.buildPublicUrl(urlSuffix)
     */
    ezBuildPublicUrl = EzNavigation.buildPublicUrl;

    /**
     * @static
     * @public @method
     * @param {string} pageName
     * @returns {string}
     */
    static buildPublicPageUrl(pageName) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.WEBSITE_PUBLIC_PATH,
                EzString.stringWithTextOrEmpty(pageName)
            ]);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzNavigation.buildPublicPageUrl(pageName)
     */
    ezGetPublicPageUrl = EzNavigation.buildPublicPageUrl;

    /**
     * @static
     * @public @method
     * Returns the full url for a page relative under the welcome url
     * @param {string} pageName
     * @returns {string}
     */
    static getWelcomePageUrl(pageName) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.WEBSITE_WELCOME_PATH,
                EzString.stringWithTextOrEmpty(pageName)
            ]);
    }

    /**
     * @public @method
     * Returns the full url for a page relative under the welcome url
     * @param {string} pageName
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzNavigation.getWelcomePageUrl(pageName)
     */
    ezGetWelcomePageUrl = EzNavigation.getWelcomePageUrl;

    /**
     * @static
     * @public @method
     * Navigates to a page relative to the welcome base url
     * @param {string} pageName
     */
    static navToWelcomePage(pageName) {
        EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzNavigation.WEBSITE_WELCOME_PATH,
                    EzString.stringWithTextOrEmpty(pageName)
                ]),
            false);
    }

    /**
     * @public @method
     * Navigates to a page relative to the welcome base url
     * @param {string} pageName
     * @deprecated
     * Migrate to static method: EzNavigation.navToWelcomePage(pageName)
     */
    ezNavigateToWelcomePage = EzNavigation.navToWelcomePage;

    /**
     * @static
     * @public @function
     * Navigates the user to a root page or url
     * @param {string} subPath
     */
    static navToRoot(subPath) {
        EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzString.EMPTY,
                    EzString.stringWithTextOrEmpty(subPath)
                ]),
            false);
    }

    /**
     * @public @function
     * Navigates the user to a root page or url
     * @param {string} subPath
     * @deprecated
     * Migrate to static function: EzNavigation.navToRoot(subPath)
     */
    ezNavigateToRoot = EzNavigation.navToRoot;

    /**
     * @static
     * @public @method
     * @param {string} pathAndFileName
     * @returns {string}
     */
    static buildNodeModulesUri(pathAndFileName) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                '/node_modules/',
                EzString.stringWithTextOrEmpty(pathAndFileName)
            ]);
    }

    /**
     * @public @method
     * @param {string} pathAndFileName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildNodeModulesUri(pathAndFileName)
     */
    ezGetNodeModulesFile = EzNavigation.buildNodeModulesUri;

    /**
     * @public @method
     * @param {string} pageName
     */
    static navToPublicPage(pageName) {
        EzNavigation.navTo(
            EzNavigation.buildPublicPageUrl(pageName),
            false);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @deprecated
     * Migrate to static function: EzNavigation.navToPublicPage(pageName)
     */
    ezNavigateToPublicPage = EzNavigation.navToPublicPage;

    /**
     * @static
     * @public @method
     * @param {string} pageName
     */
    static navToPublicMobilePage(pageName) {
        EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzNavigation.MOBILE_PUBLIC_PATH,
                    EzString.stringWithTextOrEmpty(pageName)
                ]),
            false);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @deprecated
     * Migrate to static function: EzNavigation.navToPublicMobilePage(pageName)
     */
    ezNavigateToPublicMobilePage = EzNavigation.navToPublicMobilePage;

    /**
     * @static
     * @public @method
     * Navigates the browser to the 'new' public mobile page.
     * Url template used: {EzNavigation.ezInstance.getBaseUrl()}/{EzNavigation.ezInstance.NEW_MOBILE_PUBLIC_PATH}/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {window.location}
     */
    static navToNewMobilePublicPage(urlSuffix) {
        return EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzNavigation.NEW_MOBILE_PUBLIC_PATH,
                    EzString.stringWithTextOrDefault(urlSuffix)
                ]));
    }

    /**
     * @public @method
     * Navigates the browser to the 'new' public mobile page.
     * Url template used: {EzNavigation.ezInstance.getBaseUrl()}/{EzNavigation.ezInstance.NEW_MOBILE_PUBLIC_PATH}/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {window.location}
     * @deprecated
     * Migrate to static function: EzNavigation.navToNewMobilePublicPage(urlSuffix)
     */
    ezNavigateToNewMobilePublicPage = EzNavigation.navToNewMobilePublicPage;

    /**
     * @static
     * @public @method
     * Navigates the browser to the 'new' public mobile page.
     * Url template used: {EzNavigation.ezInstance.getBaseUrl()}/{EzNavigation.ezInstance.NEW_MOBILE_PUBLIC_PATH}/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {window.location}
     */
    static navToNewMobileSecurePage(urlSuffix) {
        return EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzNavigation.NEW_MOBILE_SECURE_PATH,
                    EzString.stringWithTextOrDefault(urlSuffix)
                ]));
    }

    /**
     * @public @method
     * Navigates the browser to the 'new' public mobile page.
     * Url template used: {EzNavigation.ezInstance.getBaseUrl()}/{EzNavigation.ezInstance.NEW_MOBILE_PUBLIC_PATH}/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {window.location}
     * @deprecated
     * Migrate to static function: EzNavigation.navToNewMobileSecurePage(urlSuffix)
     */
    ezNavigateToNewMobileSecurePage = EzNavigation.navToNewMobileSecurePage;

    /**
     * @static
     * @public @method
     * Navigates to the new mobile root path of /m and appends the provided mobileUriSuffx.
     */
    static navToNewMobileUri(mobileUriSuffix) {
        return EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzNavigation.NEW_MOBILE_PATH,
                    EzString.stringWithTextOrDefault(mobileUriSuffix)
                ]));
    }

    /**
     * @public @method
     * Navigates to the new mobile root path of /m and appends the provided mobileUriSuffx.
     * @deprecated
     * Migrate to static function: EzNavigation.navToNewMobileUri(mobileUriSuffix)
     */
    ezNavigateToNewMobileUri = EzNavigation.navToNewMobileUri;

    /**
     * @static
     * @public @method
     * Returns the full url to the public mobile page
     * @param {string} pageName
     * @returns {string}
     */
    static buildPublicMobilePageUrl(pageName) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.MOBILE_PUBLIC_PATH,
                EzString.stringWithTextOrEmpty(pageName)
            ]);
    }

    /**
     * @public @method
     * Returns the full url to the public mobile page
     * @param {string} pageName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicMobilePageUrl(pageName)
     */
    ezGetPublicMobilePageUrl = EzNavigation.buildPublicMobilePageUrl;

    /**
     * @static
     * @public @method
     * Removes all the version params from the provided URI
     * @param {string} uri
     * @returns {string}
     */
    static removeVersionParamFromUri(uri) {
        if (!EzString.hasText(uri) || EzNumber.MINUS_ONE === uri.indexOf('v=')) {
            // Doesn't have a version param
            return uri;
        }

        // Split out params
        const urlAllUrlParams = uri.split('?');

        if (EzNumber.TWO !== urlAllUrlParams.length) {
            return uri;
        }

        const url = urlAllUrlParams[EzNumber.FIRST_INDEX];

        const urlParams = urlAllUrlParams[EzNumber.ONE].split('&');

        if (EzNumber.ZERO !== urlParams.length) {
            let params = EzString.EMPTY;

            for (const param of urlParams) {
                if (EzNumber.ZERO !== param.indexOf('v=')) {
                    params = EzNumber.ZERO === params.length
                        ? `?${param}`
                        : `${params}&${param}`;
                }
            }

            return `${url}${params}`;
        }

        return url;
    }

    /**
     * @public @method
     * Removes all the version params from the provided URI
     * @param {string} uri
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.removeVersionParamFromUri(uri)
     */
    ezRemoveVersionParamFromUri = EzNavigation.removeVersionParamFromUri;

    /**
     * @public @method
     * Removes the v= param from the provided url, partial url, or uri string.
     * @param {string} uri
     * @returns {string}
     * @deprecated
     * Migrate to EzNavigation.removeVersionParamFromUri(uri);
     */
    ezRemoveVersionFromUri = EzNavigation.removeVersionParamFromUri;

    /**
     * @static
     * @public @method
     * Builds a url to the 'new' public mobile page.
     * Result Template: /m/p/{urlSuffix}
     * @param {string} urlSuffix
     */
    static buildNewMobilePublicPageUrl(urlSuffix) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.NEW_MOBILE_PUBLIC_PATH,
                EzNavigation.removeVersionParamFromUri(
                    EzString.stringWithTextOrEmpty(urlSuffix))
            ]);
    }

    /**
     * @public @method
     * Builds a url to the 'new' public mobile page.
     * Result Template: /m/p/{urlSuffix}
     * @param {string} urlSuffix
     * @deprecated
     * Migrate to static function: EzNavigation.buildNewMobilePublicPageUrl(urlSuffix)
     */
    ezBuildNewMobilePublicPageUrl = EzNavigation.buildNewMobilePublicPageUrl;

    /**
     * @static
     * @public @method
     * @param {string} pageName
     */
    static navToSecureMobilePage(pageName) {
        EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzNavigation.MOBILE_SECURE_PATH,
                    EzString.stringWithTextOrEmpty(pageName)
                ]),
            false);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @deprecated
     * Migrate to static function: EzNavigation.navToSecureMobilePage(pageName)
     */
    ezNavigateToSecureMobilePage = EzNavigation.navToSecureMobilePage;

    /**
     * @static
     * @public @method
     * Displays the download mobile app page now, no mobile signin
     */
    static navToMobileSignIn() {
        const mobileSigninUri = 'm/p/download';

        axios.get(EzNavigation.signOutUrl)
            .then(
                (_response) => EzNavigation.navToPublicMobilePage(mobileSigninUri))
            .catch(
                (_err) => {
                    globalThis?.console?.error(`Signout failure: ${EzJson.toJson(_err)}`);

                    EzNavigation.navToPublicMobilePage(mobileSigninUri);
                });
    }

    /**
     * @public @method
     * Displays the download mobile app page now, no mobile signin
     * @deprecated
     * Migrate to static function: EzNavigation.navToMobileSignIn()
     */
    ezNavigateToMobileSignIn = EzNavigation.navToMobileSignIn;

    /**
     * @static
     * @public @method
     * @param {string|null} errorMessage
     * @param {string|null} userName
     */
    static navToSignIn(errorMessage, email, username) {
        const signInPage = EzUrl.build`
            signin/
            ?email=${EzString.stringWithTextOrEmpty(email)}
            &username=${EzString.stringWithTextOrEmpty(username)}
            &error=${EzString.stringWithTextOrEmpty(errorMessage)}`;

        axios.get(EzNavigation.signOutUrl)
            .then(
                (_response) => EzNavigation.navToPublicPage(signInPage))
            .catch(
                (_err) => {
                    globalThis?.console?.error(`Signout failure: ${EzJson.toJson(_err)}`);

                    EzNavigation.navToPublicPage(signInPage);
                });
    }

    /**
     * @public @method
     * @param {string|null} errorMessage
     * @param {string|null} userName
     * @deprecated
     * Migrate to static function: EzNavigation.navToSignIn(errorMessage, email, username)
     */
    ezNavigateToSignIn = EzNavigation.navToSignIn;

    /**
     * @static
     * @public @method
     * Navigates the user to the ezClocker Sign Up page
     * @param {undefined|null|string} email
     * @param {undefined|null|string} userName
     */
    static navToSignUp(email, userName) {
        EzNavigation.navToPublicPage(
            EzUrl.build`
                signup.html
                ?email=${EzString.stringWithTextOrEmpty(email)}
                &username=${EzString.stringWithTextOrEmpty(userName)}`);
    }

    /**
     * @public @method
     * Navigates the user to the ezClocker Sign Up page
     * @param {undefined|null|string} email
     * @param {undefined|null|string} userName
     * @deprecated
     * Migrate to static function: EzNavigation.navToSignUp(email, userName)
     */
    ezNavigateToSignUp = EzNavigation.navToSignUp;

    /**
     * @static
     * @public @method
     * @param {string} url
     * @param {string} queryName
     * @param {string} queryValue
     * @returns {string}
     */
    static addQueryParam(url, queryName, queryValue) {
        const queryString = EzString.hasText(queryName)
            ? `${queryName}=${EzString.stringWithTextOrEmpty(queryValue)}`
            : EzString.EMPTY;

        const urlToUse = EzString.stringWithTextOrEmpty(url);

        return EzUrl.build`${urlToUse}
           ${EzNumber.MINUS_ONE === urlToUse.indexOf('?') ? '?' : '&'}
           ${EzString.stringWithTextOrEmpty(queryString)}`;
    }

    /**
     * @public @method
     * @param {string} url
     * @param {string} queryName
     * @param {string} queryValue
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.addQueryParam(url, queryName, queryValue)
     */
    ezAddQueryParam = EzNavigation.addQueryParam;

    /**
     * @static
     * @public @method
     * Signs the currently logged in user out by navigating to the logout url.
     */
    static websiteSignOut() {
        EzNavigation.navTo(
            EzNavigation.signOutUrl,
            false);
    }

    /**
     * @public @method
     * Signs the currently logged in user out by navigating to the logout url.
     * @deprecated
     * Migrate to static function: EzNavigation.websiteSignOut()
     */
    ezSignOut = EzNavigation.websiteSignOut;

    /**
     * @static
     * @public @method
     * Navigates to the employer dashboard
     */
    static navToEmployerDashboard() {
        EzNavigation.navToSecurePage('employer/');
    }

    /**
     * @public @method
     * Navigates to the employer dashboard
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployerDashboard()
     */
    ezNavigateToEmployerDashboard = EzNavigation.navToEmployerDashboard;

    /**
     * @static
     * @public @method
     * Navigates to the employee dashboard
     */
    static navToEmployeeDashboard() {
        EzNavigation.navToSecurePage('employeeDashboard.html');
    }

    /**
     * @public @method
     * Navigates to the employee dashboard
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployeeDashboard()
     */
    ezNavigateToEmployeeDashboard = EzNavigation.navToEmployeeDashboard;

    /**
     * @static
     * @public @method
     * Navigates to the employer schedule
     */
    static navToEmployerSchedules() {
        EzNavigation.navToSecurePage('schedule/');
    }

    /**
     * @public @method
     * Navigates to the employer schedule
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployerSchedules()
     */
    ezNavigateToEmployerSchedules = EzNavigation.navToEmployerSchedules;

    /**
     * @public @method
     * Navigates to the employer time-off page
     */
    static navToTimeOff() {
        EzNavigation.navToSecurePage('time-off/');
    }

    /**
     * @public @method
     * Navigates to the employer time-off page
     * @deprecated
     * Migrate to static function: EzNavigation.navToTimeOff()
     */
    ezNavigateToTimeOff = EzNavigation.navToTimeOff;

    /**
     * @static
     * @public @method
     * Navigates to the employer schedule
     */
    static navToEmployeeSchedules() {
        EzNavigation.navToSecurePage('es.html');
    }

    /**
     * @public @method
     * Navigates to the employer schedule
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployeeSchedules()
     */
    ezNavigateToEmployeeSchedules = EzNavigation.navToEmployeeSchedules;

    /**
     * @static
     * @public @method
     * Navigates to the manage employee
     */
    static navToManageEmployees() {
        EzNavigation.navToSecurePage('manage-employees/');
    }

    /**
     * @public @method
     * Navigates to the manage employee
     * @deprecated
     * Migrate to static function: EzNavigation.navToManageEmployees()
     */
    ezNavigateToManageEmployees = EzNavigation.navToManageEmployees;

    /**
     * @static
     * @public @method
     * Navigates to the Account page
     */
    static navToEmployerAccountPage(optionalParams) {
        if (EzString.hasText(optionalParams)) {
            EzNavigation.navToSecurePage(`account?${optionalParams}`);
        } else {
            EzNavigation.navToSecurePage('account/');
        }
    }

    /**
     * @public @method
     * Navigates to the Account page
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployerAccountPage(optionalParams)
     */
    ezNavigateToEmployerAccountPage = EzNavigation.navToEmployerAccountPage;

    /**
     * @static
     * @public @method
     * Navigates to the Integrations page
     */
    static navToEmployerIntegrationsPage(optionalParams) {
        if (EzString.hasText(optionalParams)) {
            let url = EzNavigation.getSecurePageUrl('integrations')

            if (EzNumber.ZERO < url.indexOf("?")) {
                //remove cgi params
                url = url.substring(
                    EzNumber.ZERO,
                    url.indexOf("?"));
            }

            EzNavigation.navTo(
                EzUrl.build`
                    ${url}
                    ?${optionalParams}`,
                false);
        } else {
            EzNavigation.navToSecurePage('integrations/');
        }
    }

    /**
     * @public @method
     * Navigates to the Integrations page
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployerIntegrationsPage(optionalParams)
     */
    ezNavigateToEmployerIntegrationsPage = EzNavigation.navToEmployerIntegrationsPage;

    /**
     * @static
     * @public @method
     * Navigates to the base url
     */
    static navToMain() {
        EzNavigation.navTo(
            EzNavigation.buildBaseUrl());
    }

    /**
     * @public @method
     * Navigates to the base url
     * @deprecated
     * Migrate to static function: EzNavigation.navToMain()
     */
    ezNavigateToMain = EzNavigation.navToMain;

    /**
     * @static
     * @public @method
     * Navigates to the public ezClocker landing page
     */
    static navToLandingPage() {
        EzNavigation.navTo(
            EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    '/welcome'
                ]),
            false);
    }

    /**
     * @public @method
     * Navigates to the public ezClocker landing page
     * @deprecated
     * Migrate to static function: EzNavigation.navToLandingPage()
     */
    ezNavigateToLandingPage = EzNavigation.navToLandingPage;

    /**
     * @static
     * @public @readonly @property
     * Gets the ezclocker logout url
     * @returns {string}
     */
    static get signOutUrl() {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.SIGN_OUT_PATH
            ]);
    }

    /**
     * @public @method
     * Gets the ezclocker logout url
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzNavigation.signOutUrl
     */
    ezGetSignOutUrl = () => EzNavigation.signOutUrl;

    /**
     * @static
     * @public @readonly @property
     * Obtains the sign-in url for ezClocker
     * @returns {string}
     */
    static get signinUrl() {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.SIGN_IN_PATH
            ]);
    }

    /**
     * @public @method
     * Obtains the sign-in url for ezClocker
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzNavigation.signinUrl
     */
    ezGetSigninUrl = () => EzNavigation.signinUrl;

    /**
     * @static
     * @public @method
     * Returns the full url for a internal classic api endpoint
     * @param {string} serviceName
     * Name of the service (without the underscore prefix)
     * @returns {string}
     */
    static buildInternalClassicApiUrl(serviceName) {
        return EzBoolean.isTrue(EzNavigation.ezIsDeveloperDocs)
            ? EzNavigation.getPublicClassicApiUrl(serviceName)
            : EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    `${EzNavigation.INTERNAL_CLASSIC_API_PATH_PREFIX}${EzString.stringWithTextOrEmpty(serviceName)}`
                ]);
    }

    /**
     * @static
     * @public @method
     * Returns the full url for a internal classic api endpoint
     * @param {string} serviceName
     * Name of the service (without the underscore prefix)
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildInternalClassicApiUrl(serviceName)
     */
    static getInternalClassicApiUrll = EzNavigation.buildInternalClassicApiUrl;

    /**
     * @public @method
     * Returns the full url for a internal classic api endpoint
     * @param {string} serviceName
     * Name of the service (without the underscore prefix)
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildInternalClassicApiUrl(serviceName)
     */
    ezGetInternalClassicApiUrl = EzNavigation.buildInternalClassicApiUrl;

    /**
     * @public @method
     * Builds an internal api url. If apiVersion is not provided, the default api version is used.
     * Template: {domain}/_api/{apiVersion}/{urlSuffix}
     * @param {string} urlSuffix
     * @param {String|null} apiVersion
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildInternalApiUrl(serviceName, version, noFileRevision = false)
     */
    ezBuildInternalApiUrl = EzNavigation.buildInternalApiUrl;

    /**
     * @static
     * @public @method
     * Returns the url for an internal api
     * @param {string} serviceName
     * @param {string|null} version
     * @returns {string}
     */
    static buildInternalApiUrl(serviceName, version, noFileRevision = true) {
        if (!EzString.hasText(version)) {
            version = EzNavigation.DEFAULT_INTERNAL_API_VERSION;
        }

        return EzNavigation.ezIsDeveloperDocs
            ? EzNavigation.buildPublicApiUrl(
                serviceName,
                version,
                noFileRevision)
            : EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    EzNavigation.INTERNAL_API_PATH_PREFIX,
                    EzString.stringWithTextOrDefault(version),
                    EzString.stringWithTextOrDefault(serviceName)
                ],
                noFileRevision);
    }

    /**
     * @static
     * @public @method
     * Returns the url for an internal api
     * @param {string} serviceName
     * @param {string|null} version
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildInternalApiUrl(serviceName, version)
     */
    static getInternalApiUrl = EzNavigation.buildInternalApiUrl;

    /**
     * @public @method
     * Returns the url for an internal api
     * @param {string} serviceName
     * @param {string|null} version
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildInternalApiUrl(serviceName, version)
     */
    ezGetInternalApiUrl = EzNavigation.buildInternalApiUrl;

    /**
     * @static
     * Builds a classic public api url.
     * @public @method
     * @param {string} serviceName
     * @returns {string}
     */
    static buildPublicClassicApiUrl(serviceName) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzString.stringWithTextOrDefault(serviceName)
            ],
            true);
    }

    /**
     * @static
     * Builds a classic public api url.
     * @public @method
     * @param {string} serviceName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicClassicApiUrl(serviceName)
     */
    static getPublicClassicApiUrl = EzNavigation.buildPublicClassicApiUrl;

    /**
     * @public @method
     * @param {string} serviceName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicClassicApiUrl(serviceName)
     */
    ezGetPublicClassicApiUrl = EzNavigation.buildPublicClassicApiUrl;

    /**
     * @static
     * @public @method
     * Obtains the full Url to an ezclocker public api
     * @param {string} serviceName
     * @param {string} version
     */
    static buildPublicApiUrl(serviceName, version, noFileRevision = true) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzNavigation.PUBLIC_API_PATH_PREFIX,
                EzString.hasText(version)
                    ? version
                    : EzNavigation.DEFAULT_INTERNAL_API_VERSION,
                EzString.stringWithTextOrDefault(serviceName)
            ],
            noFileRevision);
    }

    /**
     * @public @method
     * Obtains the full Url to an ezclocker public api
     * @param {string} serviceName
     * @param {string} version
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicApiUrl(serviceName, version)
     */
    ezGetPublicApiUrl = EzNavigation.buildPublicApiUrl;

    /**
     * @static
     * Builds a public adminisration service url
     * @public @method
     * @param {string} subServiceName
     * @returns {string}
     */
    static buildPublicAdminServiceUrl(subServiceName) {
        return EzString.hasText(subServiceName)
            ? EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    '/api/v1/admin',
                    subServiceName
                ])
            : EzNavigation.buildUrl(
                EzNavigation.buildBaseUrl(),
                [
                    '/api/v1/admin'
                ]);
    }

    /**
     * @static
     * Builds a public adminisration service url
     * @public @method
     * @param {string} subServiceName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.getPublicAdminServiceUrl(subServiceName)
     */
    static getPublicAdminServiceUrl = EzNavigation.buildPublicAdminServiceUrl;

    /**
     * @public @method
     * @param {string} subServiceName
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.getPublicAdminServiceUrl(subServiceName)
     */
    ezGetPublicAdminServiceUrl = EzNavigation.getPublicAdminServiceUrl;

    /**
     * @static
     * @public @fucntion
     * Gets the full url to use for the provided imageSubPathAndName
     * @param {string} imageSubPathAndName
     * Default: EzString.EMPTY
     * @returns {string}
     */
    static buildPublicImageUrl(imageSubPathAndName = EzString.EMPTY) {
        const imageBaseUrl = EzUrl.build`
            ${EzNavigation.buildStaticResourceBaseUrl()}
            /images`;

        if (!EzString.hasText(imageSubPathAndName)) {
            return EzNavigation.buildUrl(imageBaseUrl);
        }

        let imageSubpathAndNameToUse = imageSubPathAndName.replace('public/', EzString.EMPTY)
            .replace('images/', EzString.EMPTY);

        if (!imageSubpathAndNameToUse.startsWith('/')) {
            imageSubpathAndNameToUse = `/${imageSubpathAndNameToUse}`;
        }

        return EzNavigation.buildUrl(
            imageBaseUrl,
            [
                imageSubpathAndNameToUse
            ]);
    }

    /**
     * @static
     * @public @fucntion
     * Gets the full url to use for the provided imageSubPathAndName
     * @param {string} imageSubPathAndName
     * Default: EzString.EMPTY
     * @returns {string}
     * @deprecated
     * Migreate to static function: EzNavigation.buildPublicImageUrl(imageSubPathAndName)
     */
    static getPublicImagesUrl = EzNavigation.buildPublicImageUrl;

    /**
     * @public @fucntion
     * Gets the full url to use for the provided imageSubPathAndName
     * @param {string} imageSubPathAndName
     * @returns {string}
     * @deprecated
     * Migreate to static function: EzNavigation.buildPublicImageUrl(imageSubPathAndName)
     */
    ezGetPublicImagesUrl = EzNavigation.buildPublicImageUrl;

    /**
     * @static
     * @public @method
     * Builds the ezClocker website's static resources URL
     * @returns {string}
     */
    static buildStaticResourceBaseUrl() {
        const environmentUrls = {
            dev: (href) => EzNumber.MINUS_ONE !== href.indexOf('dev2.ezclocker.com')
                ? 'https://website.dev.ezclocker.com/dev2'
                : `https://website.dev.ezclocker.com/${EzNavigation.environment}`,
            loc: (env) => `https://website.dev.ezclocker.com/${env}`,
            qal: (env) => `https://website.dev.ezclocker.com/${env}`,
            e2e: (env) => `https://website.dev.ezclocker.com/${env}`,
            prf: (env) => `https://website.dev.ezclocker.com/${env}`,
            stg: () => 'https://website.prd.ezclocker.com/prd',
            prd: () => 'https://website.prd.ezclocker.com/prd'
        };

        return (environmentUrls[EzNavigation.environment] || environmentUrls.prd)(
            EzNavigation.environment,
            window.location.href
        );
    }

    /**
     * @static
     * @public @method
     * Builds the ezClocker website's static resources URL
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildStaticResourceBaseUrl()
     */
    getStaticResourcesBaseUrl = EzNavigation.buildStaticResourceBaseUrl;

    /**
     * @public @method
     * Builds the ezClocker website's static resources URL
     * @returns {string}
     * @deprecated
     * Migrate to static function: EzNavigation.buildStaticResourceBaseUrl()
     */
    ezGetStaticResourcesBaseUrl = EzNavigation.buildStaticResourceBaseUrl;

    /**
     * @static
     * @public @method
     * Navigates to the mobile site to the provided page
     * @param {string} page
     */
    static redirectToMobileSitePage(page) {
        if (!EzString.hasText(page)) {
            // no where to navigate to
            return false;
        }

        if (EzObject.isValid(jQuery?.browser?.mobile)) {
            EzNavigation.navToPublicMobilePage(page);

            return true;
        }

        return false;
    }

    /**
     * @public @method
     * Navigates to the mobile site to the provided page
     * @param {string} url
     * @deprecated
     * Migrate to static function: EzNavigation.redirectToMobileSitePage(url)
     */
    ezRedirectToMobileSitePage = EzNavigation.redirectToMobileSitePage;

    /**
     * @static
     * @public @method
     * Uses the re-direct service to navigate a user to their dashboard
     * @param {boolean|null} mobile
     */
    static redirectToDashboard(mobile) {
        EzNavigation.navTo(
            EzNavigation.buidlUrlFromBase(
                EzBoolean.isTrue(mobile)
                    ? 'redirectToDashboard?mobile=true'
                    : 'redirectToDashboard'));
    }

    /**
     * @public @method
     * Uses the re-direct service to navigate a user to their dashboard
     * @param {boolean|null} mobile
     * @deprecated
     * Migrate to static method: EzNavigation.redirectToDashboard(mobile)
     */
    ezRedirectToDashboard = EzNavigation.redirectToDashboard;

    /**
     * @static
     * @public @method
     * Navigates to a secure HTML page
     * @param {string} pageName
     */
    static navToSecurePage(pageName) {
        EzNavigation.navTo(
            EzNavigation.buildSecurePageUrl(pageName),
            false);
    }

    /**
     * @public @method
     * Navigates to a secure HTML page
     * @param {string} pageName
     * @deprecated
     * Migrate to static method: EzNavigation.navToSecurePage()
     */
    ezNavigateToSecurePage = EzNavigation.navToSecurePage;

    /**
     * @public @method
     * Navigates to a secure HTML page
     * @param {string} pageName
     * @deprecated
     * Migrate to static method: EzNavigation.navToSecurePage()
     */
    navigateToSecurePage(pageName) {
        EzNavigation.navToSecurePage(pageName);
    }

    /**
     * @public @method
     * @param {string} subserviceName
     * @deprecated
     * Migrate to EzNavigation.ezInstance.ezGetPublicAdminServiceUrl()
     */
    getPublicAdminServiceUrl(subserviceName) {
        return EzNavigation.getPublicAdminServiceUrl(subserviceName);
    }

    /**
     * @public @method
     * Signs out the current user without notifications
     * @returns {promise}
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezSignOut()
     */
    ezSilentSignOut() {
        return axios.get(EzNavigation.signOutUrl);
    }

    /**
     * @public @method
     * Signs in a user using the user name and password without prompting them for input.
     * @param {string} username
     * @param {string} password
     * @param {function} success
     * @param {function} failure
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezAccountServices.ezSilentSignIn()
     */
    silentSignIn(username, password, success, failure) {
        axios.post(
            EzUrl.build`
                ${EzNavigation.SIGN_IN_PATH}
                ?j_username=${EzUrl.encodeURL(username)}
                &j_password=${EzUrl.encodeURL(password)}`)
            .then(
                (response) => EzFunction.callback(success, response))
            .catch(
                (err) => {
                    globalThis.ezApi.ezclocker.ezLogger.error(
                        `Failed to silently sign in due to the following error: ${err.message}.`);

                    EzFunction.callback(
                        failure,
                        {
                            errorCode: 500,
                            message: err.message,
                            error: err
                        });
                });
    }

    /**
     * @public @method
     * Signs in a user without notifications
     * @param {string} username
     * @param {string} password
     * @returns {promise}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezAccountServices.ezSilentSignIn()
     */
    ezSilentSignIn(username, password) {
        if (EzString.hasText(username) && EzString.hasLength(password)) {
            return axios.post(
                EzUrl.build`
                    ${EzNavigation.signinUrl}
                    ?j_username=${username}
                    &j_password=${password}`);
        }

        return EzPromise.reject(
            {
                errorCode: 401,
                message: 'Username and/or password is incorrect.'
            });
    }

    /**
     * @static
     * @public @method
     * Returns a full url for the provided service path
     * @param {string} servicePath
     * @returns {string}
     */
    static buildServiceUrl(servicePath) {
        return EzNavigation.buildUrl(
            EzNavigation.buildBaseUrl(),
            [
                EzString.stringWithTextOrEmpty(servicePath)
            ]);
    }

    /**
     * @public @method
     * Returns a full url for the provided service path
     * @param {string} servicePath
     * @returns {string}
     * @deprecated
     * Migrate to:
     * EzNavigation.buildServiceUrl(servicePath)
     */
    getServiceUrl = EzNavigation.buildServiceUrl;

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: Migrate to EzNavigation.buildInternalClassicApiUrl()
     */
    getInternalClassicApiUrl(serviceName) {
        return EzNavigation.buildInternalClassicApiUrl(serviceName);
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: Migrate to EzNavigation.buildInternalClassicApiUrl()
     */
    getInternalClassicServiceUrl(serviceName) {
        return EzNavigation.buildInternalClassicApiUrl(serviceName);
    }

    /**
     * @deprecated
     * Migrate to static function: Migrate to EzNavigation.buildInternalApiUrl()
     */
    getInternalApiUrl(serviceName, version) {
        return EzNavigation.buildInternalApiUrl(serviceName, version);
    }

    /**
     * @public @method
     * @param {string} serviceName
     * @param {string} version
     * @deprecated
     * Migrate to static function: Migrate to EzNavigation.buildInternalApiUrl()
     */
    ezGetInternalApiServiceUrl(serviceName, version) {
        return EzNavigation.buildInternalApiUrl(serviceName, version);
    }

    /**
     * @public @method
     * @param {string} serviceName
     * @param {string} version
     * @deprecated
     * Migrate to static function: EzNavigation.buildInternalApiUrl()
     */
    getInternalApiServiceUrl(serviceName, version) {
        return EzNavigation.buildInternalApiUrl(serviceName, version);
    }

    /**
     * @public @method
     * @param {string} serviceName
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicClassicApiUrl()
     */
    getPublicClassicServiceUrl(serviceName) {
        return EzNavigation.buildPublicClassicApiUrl(serviceName);
    }

    /**
     * @public @function
     * @param {string} serviceName
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicClassicApiUrl()
     */
    ezGetPublicClassicServiceUrl(serviceName) {
        return EzNavigation.buildPublicClassicApiUrl(serviceName);
    }

    /**
     * @public @method
     * @param {string} serviceName
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicClassicApiUrl(serviceName)
     */
    getPublicClassicApiUrl(serviceName) {
        return EzNavigation.buildPublicClassicApiUrl(serviceName);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicApiUrl(serviceName, version)
     */
    getPublicApiServiceUrl(path) {
        return EzNavigation.buildPublicApiUrl(path);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicApiUrl(serviceName, version)
     */
    ezGetPublicApiServiceUrl(path) {
        return EzNavigation.buildPublicApiUrl(path);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicApiUrl(serviceName, version)
     */
    getPublicApiUrl(path) {
        return EzNavigation.buildPublicApiUrl(path);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.buidlUrlFromBase(urlSnip)
     */
    getUrlFromBase(path) {
        return EzNavigation.buidlUrlFromBase(path);
    }

    /**
     * @public @method
     * @param {string} imageName
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicImageUrl(imageSubPathAndName = EzString.EMPTY)
     */
    getPublicImagesUrl(imageName) {
        return EzNavigation.buildPublicImageUrl(imageName);
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to one of the following static properties:
     * 1) (preferred) EzNavigation.environment
     * 2) EzNavigation.websiteEnvironment
     */
    getEnvironment() {
        return EzNavigation.websiteEnvironment
    }

    /**
     * @public @method
     * @param {string} path
     * @param {boolean} skipRevision
     * Default: false
     * @deprecated
     * Migrate to EzNavigation.navTo(url, skipRevision = false)
     */
    navigateTo(path, skipRevision = false) {
        EzNavigation.navTo(path, skipRevision);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @deprecated
     * Migrate to static function: EzNavigation.buildSecurePageUrl(pageName)
     */
    getSecurePageUrl(pageName) {
        return EzNavigation.buildSecurePageUrl(pageName);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicPageUrl(pageName)
     */
    getPublicPageUrl(pageName) {
        return EzNavigation.buildPublicPageUrl(pageName);
    }

    /**
     * @public @method
     * @param {string} pathAndFileName
     * @deprecated
     * Migrate to static function: EzNavigation.buildNodeModulesUri(pathAndFileName)
     */
    getNodeModulesFile(pathAndFileName) {
        return EzNavigation.buildNodeModulesUri(pathAndFileName);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.navToPublicPage(pageName)
     */
    navigateToPublicPage(path) {
        return EzNavigation.navToPublicPage(path);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.navToPublicMobilePage(path))
     */
    navigateToPublicMobilePage(path) {
        return EzNavigation.navToPublicMobilePage(path);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.buildPublicMobilePageUrl(path)
     */
    getPublicMobilePageUrl(path) {
        return EzNavigation.buildPublicMobilePageUrl(path);
    }

    /**
     * @public @method
     * @param {string} path
     * @deprecated
     * Migrate to static function: EzNavigation.navToSecureMobilePage(...)
     */
    navigateToSecureMobilePage(path) {
        return EzNavigation.navToSecureMobilePage(path);
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.navToMobileSignIn()
     */
    navigateToMobileSignIn() {
        return EzNavigation.navToMobileSignIn();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.navToSignIn()
     */
    navigateToSignIn() {
        return EzNavigation.navToSignIn();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.websiteSignOut
     */
    signOut() {
        return EzNavigation.websiteSignOut();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.websiteSignOut
     */
    silentSignOut() {
        return EzNavigation.websiteSignOut();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployerDashboard()
     */
    navigateToEmployerDashboard() {
        return EzNavigation.navToEmployerDashboard()
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function:  EzNavigation.navToEmployeeDashboard()
     */
    navigateToEmployeeDashboard() {
        return EzNavigation.navToEmployeeDashboard();
    }

    /**
     * @public @function
     * @deprecated
     * Migrate to static function:  EzNavigation.navToEmployerSchedules()
     */
    navigateToEmployerSchedules() {
        return EzNavigation.navToEmployerSchedules();
    }


    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.navToManageEmployees()
     */
    navigateToManageEmployees() {
        return EzNavigation.navToManageEmployees();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.navToEmployerAccountPage()
     */
    navigateToEmployerAccountPage() {
        return EzNavigation.navToEmployerAccountPage();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.navToMain()
     */
    navigateToMain() {
        return EzNavigation.navToMain();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static function: EzNavigation.navToLandingPage()
     */
    navigateToLandingPage() {
        return EzNavigation.navToLandingPage();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static fucntion: EzNavigation.buildBaseUrl()
     */
    getBaseUrl() {
        return EzNavigation.buildBaseUrl();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static property: EzNavigation.signOutUrl
     */
    ezGetLogoutUrl() {
        return EzNavigation.signOutUrl;
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static property: EzNavigation.signOutUrl
     */
    getLogoutUrl() {
        return EzNavigation.signOutUrl;
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to static property: EzNavigation.getSigninUrl()
     */
    getSigninUrl() {
        return EzNavigation.signinUrl;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezNavigation';
    }

    /**
     * @public @method @static @readonly @property
     * Gets the ezApi legacy short name for the singleton instance
     * @returns {string}
     */
    static get ezApiShortName() {
        return 'nav';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzNavigation_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     * @type {EzNavigation}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]
        ? globalThis.ezApi.ezclocker[EzNavigation.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzNavigation}
     */
    static get ezInstance() {
        return EzNavigation.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzNavigation} instance
     */
    static set ezInstance(instance) {
        if (null != EzNavigation.#ezInstance) {
            throw new Error('EzNavigation\'s singleton instance is already reigstered with EzApi.');
        }

        EzNavigation.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]
        ? EzRegistrationState.REGISTERED
        : EzRegistrationState.NOT_REGISTERED;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzNavigation.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzNavigation.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES:
     *  Safe to ignore SonarLint hint javascript:S1541
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzNavigation.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzHttpHelper.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzNavigation.ezInstance &&
            EzRegistrationState.ezIsRegisteredState(EzNavigation.ezApiRegistrationState);
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzNavigation.#ezCanRegister && !EzNavigation.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(
                EzNavigation,
                EzNavigation.ezApiName,
                false,
                [EzNavigation.ezApiShortName]);
        }

        return EzNavigation.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzNavigation.ezApiName
     *     2) Property getter EzNavigation.ezEventNames
     *     3) Property getter EzNavigation.ezInstance
     *     4) Property setter EzNavigation.ezInstance
     *     5) Property getter EzNavigation.ezApiRegistrationState
     *     6) Property setter EzNavigation.ezApiRegistrationState
     *     7) Property getter EzNavigation.#ezCanRegister
     *     8) Property getter EzNavigation.#ezIsRegistered
     *     9) Method EzNavigation.#ezRegistrator()
     */
    static {
        if (!EzNavigation.#ezIsRegistered) {
            EzNavigation.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzNavigation.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzNavigation.ezOnEzApiReadyEventName,
                    EzNavigation.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzHttpHelper.ezEventNames.onReady,
                    EzNavigation.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
